"use client";

import { Mail } from "lucide-react";
import * as Sentry from "@sentry/nextjs";
import { useMemo, type ReactNode } from "react";
import { JOB_CONTACT_FORM_MAX_FILE_SIZE_BYTES } from "@/app/config/contact-form-file-limits";
import { ContactForm as ContactFormBase } from "@/app/components/forms/contact/contact-form";
import type { ContactFormSubmitMetaT } from "@/app/components/forms/contact/contact-form";
import { submitContactForm } from "@/app/services/server/actions/contact-form/submit-contact-form";
import { uploadContactFormDocument } from "@/app/services/server/actions/contact-form/upload-document";
import type {
  ContactFormSelectOptionT,
  ContactFormConfigT,
  ContactFormFieldTypeT,
  ContactFormFieldConfigT,
} from "./contact-form-types";
import type { DefaultValues } from "react-hook-form";
import type { ZodSchema } from "zod";
import {
  getContactFormVariantDefinition,
  type ContactFormVariantContextRequirementT,
  type ContactFormVariantContextT,
  type ContactFormVariantFieldNameT,
  type ContactFormVariantFormDataT,
  type ContactFormVariantKeyT,
  type ContactFormVariantSelectFieldNameT,
  type SelectedSelectMetaT,
} from "./contact-form-variant-registry";
import { useBrand } from "@/app/components/theme/brand-theme-boundary";

type ContactFormVariantContextProp<TVariant extends ContactFormVariantKeyT> =
  ContactFormVariantContextRequirementT<TVariant> extends true
    ? { variantContext: ContactFormVariantContextT<TVariant> }
    : { variantContext?: ContactFormVariantContextT<TVariant> };

type LockedSelectValuesT<TVariant extends ContactFormVariantKeyT> = Partial<
  Record<ContactFormVariantSelectFieldNameT<TVariant>, string>
>;

type ContactFormVariantProps<TVariant extends ContactFormVariantKeyT> =
  ContactFormVariantContextProp<TVariant> & {
    variant: TVariant;
    runtimeSelectOptions?: Partial<
      Record<
        ContactFormVariantSelectFieldNameT<TVariant>,
        ContactFormSelectOptionT[]
      >
    >;
    submitOptions?: {
      skipWebhook?: boolean;
    };
    onSubmit?: (
      values: ContactFormVariantFormDataT<TVariant>,
      meta?: ContactFormSubmitMetaT
    ) => Promise<{ success: boolean; errorMessage?: string }>;
    className?: string;
    submitButtonVariant?: "default" | "secondary" | "outline";
    presentation?: "card" | "plain";
    showHeader?: boolean;
    lockedSelectValues?: LockedSelectValuesT<TVariant>;
    defaultValueOverrides?: DefaultValues<
      ContactFormVariantFormDataT<TVariant>
    >;
    header?: ReactNode;
    renderFooter?: (params: {
      isSubmitting: boolean;
      submitButton: ReactNode;
    }) => ReactNode;
  };

export function ContactFormVariant<TVariant extends ContactFormVariantKeyT>({
  variant,
  variantContext,
  runtimeSelectOptions,
  submitOptions,
  onSubmit,
  className,
  submitButtonVariant = "default",
  presentation,
  showHeader = true,
  lockedSelectValues,
  defaultValueOverrides,
  header,
  renderFooter,
}: ContactFormVariantProps<TVariant>) {
  const { isSkoda, isAudi } = useBrand();
  const variantDefinition = getContactFormVariantDefinition(variant);
  const addFormBreadcrumb = (
    message: string,
    data?: Record<string, unknown>
  ) => {
    Sentry.addBreadcrumb({
      category: "contact-form",
      message,
      level: "info",
      data: {
        variant,
        hasContext: Boolean(variantContext),
        pathname: typeof window !== "undefined" ? window.location.pathname : "",
        ...data,
      },
    });
  };

  const configSelectOptionMap = useMemo(() => {
    const map = new Map<string, ContactFormSelectOptionT[]>();

    variantDefinition.config.sections.forEach((section) => {
      section.fields.forEach((field) => {
        const typedField = field as {
          name: string;
          type: ContactFormFieldTypeT;
          options?: ContactFormSelectOptionT[];
        };

        if (typedField.type === "select" && Array.isArray(typedField.options)) {
          map.set(typedField.name, typedField.options);
        }
      });
    });

    return map;
  }, [variantDefinition.config.sections]);

  const shouldHideDefaultHeaderIcon = isSkoda || isAudi;
  const resolvedHeader = showHeader
    ? (header ??
      (shouldHideDefaultHeaderIcon ? undefined : (
        <div className="mb-8 flex items-center gap-3">
          <div className="bg-light-blue flex h-12 w-12 items-center justify-center rounded-full">
            <Mail className="text-primary h-6 w-6" />
          </div>
        </div>
      )))
    : undefined;

  const resolvedPresentation =
    presentation ?? variantDefinition.ui.defaultPresentation;

  // Extend defaultValues with make and aktion from context for offer/vehicle variants
  const resolvedDefaultValues = useMemo(() => {
    const baseDefaults = variantDefinition.defaultValues as DefaultValues<
      ContactFormVariantFormDataT<TVariant>
    >;
    const extendedDefaults: Record<string, unknown> = {
      ...baseDefaults,
    };

    if (
      (variant === "offer" || variant === "vehicle") &&
      variantContext &&
      "offer" in variantContext &&
      variantContext.offer
    ) {
      if (variantContext.offer.make) {
        extendedDefaults.make = variantContext.offer.make;
      }

      if (variantContext.offer.aktion) {
        extendedDefaults.aktion = variantContext.offer.aktion;
      }

      if (variantContext.offer.vehicleCondition) {
        extendedDefaults.vehicleCondition =
          variantContext.offer.vehicleCondition;
      }

      if (variantContext.offer.locationId) {
        extendedDefaults.location = variantContext.offer.locationId;
      }
    }

    // Extend defaultValues with vehicleCondition and company from context for company variant
    if (
      variant === "company" &&
      variantContext &&
      "companyName" in variantContext
    ) {
      if (variantContext.vehicleCondition) {
        extendedDefaults.vehicleCondition = variantContext.vehicleCondition;
      }

      if (variantContext.companyName) {
        extendedDefaults.company = variantContext.companyName;
      }
    }

    return {
      ...extendedDefaults,
      ...(defaultValueOverrides as Record<string, unknown> | undefined),
      ...(lockedSelectValues as Record<string, string> | undefined),
    } as DefaultValues<ContactFormVariantFormDataT<TVariant>>;
  }, [
    lockedSelectValues,
    defaultValueOverrides,
    variant,
    variantContext,
    variantDefinition.defaultValues,
  ]);

  const resolvedConfig = useMemo(() => {
    if (!lockedSelectValues || Object.keys(lockedSelectValues).length === 0) {
      return variantDefinition.config;
    }

    return {
      ...variantDefinition.config,
      sections: variantDefinition.config.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          const lockedValue = (lockedSelectValues as Record<string, string>)[
            field.name
          ];

          if (field.type !== "select" || !lockedValue) {
            return field;
          }

          return {
            ...field,
            disabled: true,
          } satisfies ContactFormFieldConfigT<string>;
        }),
      })),
    };
  }, [lockedSelectValues, variantDefinition.config]);

  const handleSubmit =
    onSubmit ??
    (async (
      values: ContactFormVariantFormDataT<TVariant>,
      meta?: ContactFormSubmitMetaT
    ): Promise<{ success: boolean; errorMessage?: string }> => {
      addFormBreadcrumb("submit-start");
      const selectMeta = (() => {
        const meta: SelectedSelectMetaT = {};
        const runtimeOptionsMap = runtimeSelectOptions as
          | Record<string, ContactFormSelectOptionT[]>
          | undefined;

        (["location", "make", "makeName", "contactPerson"] as const).forEach(
          (fieldName) => {
            const fieldValue = (values as Record<string, unknown>)[fieldName];

            if (
              typeof fieldValue !== "string" ||
              fieldValue.trim().length === 0
            ) {
              return;
            }

            const normalizedValue = fieldValue.trim();

            const runtimeLabel = runtimeOptionsMap?.[fieldName]?.find(
              (option) => option.value === normalizedValue
            )?.label;

            const configLabel = configSelectOptionMap
              .get(fieldName)
              ?.find((option) => option.value === normalizedValue)?.label;

            meta[fieldName] = {
              id: normalizedValue,
              name: runtimeLabel ?? configLabel ?? normalizedValue,
            };
          }
        );

        return meta;
      })();

      // Upload document if it's a File object (for companyOffer variant with abrufschein)
      const processedValues = { ...values };
      if (
        variant === "companyOffer" &&
        "abrufschein" in processedValues &&
        processedValues.abrufschein instanceof File
      ) {
        const uploadResult = await uploadContactFormDocument(
          processedValues.abrufschein as File
        );

        if (!uploadResult.success) {
          addFormBreadcrumb("upload-failed", {
            stage: "abrufschein",
          });
          return {
            success: false,
            errorMessage:
              uploadResult.error ?? "Fehler beim Hochladen der Datei",
          };
        }

        // Replace File with URL string
        (processedValues as Record<string, unknown>).abrufschein =
          uploadResult.url;
        addFormBreadcrumb("upload-success", {
          stage: "abrufschein",
        });
      }

      if (variant === "job") {
        const uploadDefinitions = [
          {
            key: "cv",
            stage: "cv",
            folder: "jobs/cv",
            fileNamePrefix: "cv",
          },
          {
            key: "certificates",
            stage: "certificates",
            folder: "jobs/certificates",
            fileNamePrefix: "certificates",
          },
        ] as const;

        for (const uploadDefinition of uploadDefinitions) {
          const uploadValue = (processedValues as Record<string, unknown>)[
            uploadDefinition.key
          ];

          if (!(uploadValue instanceof File)) {
            continue;
          }

          const uploadResult = await uploadContactFormDocument(uploadValue, {
            folder: uploadDefinition.folder,
            fileNamePrefix: uploadDefinition.fileNamePrefix,
            maxFileSizeBytes: JOB_CONTACT_FORM_MAX_FILE_SIZE_BYTES,
          });

          if (!uploadResult.success) {
            addFormBreadcrumb("upload-failed", {
              stage: uploadDefinition.stage,
            });
            return {
              success: false,
              errorMessage:
                uploadResult.error ?? "Fehler beim Hochladen der Datei",
            };
          }

          (processedValues as Record<string, unknown>)[uploadDefinition.key] =
            uploadResult.url;
          addFormBreadcrumb("upload-success", {
            stage: uploadDefinition.stage,
          });
        }
      }

      const result = await submitContactForm({
        variant,
        data: processedValues,
        botProtection: {
          startedAtMs: meta?.startedAtMs,
        },
        context: variantContext,
        selectMeta,
        skipWebhook: submitOptions?.skipWebhook,
      });

      if (!result.success) {
        addFormBreadcrumb("submit-failed", {
          hasError: Boolean(result.error),
        });
        return {
          success: false,
          errorMessage:
            result.error ?? "Ein unbekannter Fehler ist aufgetreten.",
        };
      }

      addFormBreadcrumb("submit-success");
      return { success: true };
    });

  return (
    <ContactFormBase<
      ContactFormVariantFormDataT<TVariant>,
      ContactFormVariantFieldNameT<TVariant>,
      ContactFormVariantSelectFieldNameT<TVariant>
    >
      schema={
        variantDefinition.schema as ZodSchema<
          ContactFormVariantFormDataT<TVariant>
        >
      }
      defaultValues={resolvedDefaultValues}
      config={
        resolvedConfig as ContactFormConfigT<
          ContactFormVariantFieldNameT<TVariant>
        >
      }
      selectFieldNames={
        variantDefinition.selectFieldNames as readonly ContactFormVariantSelectFieldNameT<TVariant>[]
      }
      runtimeSelectOptions={runtimeSelectOptions}
      onSubmit={handleSubmit}
      header={resolvedHeader}
      presentation={resolvedPresentation}
      className={className}
      submitButtonVariant={submitButtonVariant}
      renderFooter={renderFooter}
    />
  );
}
